import { search } from "algolia-instant-search";

const { connectInfiniteHits } = instantsearch.connectors;

// Algolia's index stores the raw, full-resolution Shopify CDN image URL — request a
// properly-sized, WebP variant instead of shipping the original to every grid card.
const PRODUCT_IMAGE_WIDTHS = [200, 320, 480, 640, 960];
function shopifyImageUrl(url, width) {
  if (!url) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}width=${width}&format=webp`;
}

if (!customElements.get("algolia-infinite-hits")) {
  class AlgoliaInfiniteHits extends HTMLElement {
    connectedCallback() {
      this.selectElements();

      const customInfiniteHits = connectInfiniteHits(
        this.renderInfiniteHits.bind(this),
      );

      search.addWidgets([customInfiniteHits({ container: this.content })]);
    }

    selectElements() {
      this.productCardTemplate = this.querySelector("template#product-card");
      this.loadMoreButtonTemplate = this.querySelector(
        "template#load-more-button",
      );
      this.content = this.querySelector("#main-product-grid");
      this.noResultsText = this.content?.dataset.text || "No products found.";
      this.placeholderImage = this.dataset.placeholderImage || "";
      this.collectionHandle = this.dataset.collectionHandle || "";
      this.catalogGids = JSON.parse(this.dataset.catalogGids || "[]");
      // Bumped at the START of EVERY render (empty AND non-empty) and on a manual
      // clear, so a slow verification query that resolves after any later render
      // detects it's stale and bails — never painting over now-valid results.
      this._renderSeq = 0;
    }

    renderInfiniteHits(renderOptions, isFirstRender) {
      const { hits, showMore, isLastPage, widgetParams } = renderOptions;

      // Invalidate any in-flight verification query: EVERY render (empty or not)
      // supersedes the previous view. A non-empty render arriving after a clear /
      // filter change bumps this so the stale verifier can't repaint the empty state.
      this._renderSeq++;

      if (isFirstRender) {
        const loadMoreClone =
          this.loadMoreButtonTemplate.content.cloneNode(true);
        widgetParams.container.parentNode.insertBefore(
          loadMoreClone,
          widgetParams.container.nextSibling,
        );

        const loadMoreContainer =
          widgetParams.container.parentNode.querySelector(
            ".collection__load-more-wrap",
          );
        const button = loadMoreContainer.querySelector(
          ".collection__load-more-btn",
        );
        button.addEventListener("click", () => showMore());
        widgetParams.loadMoreContainer = loadMoreContainer;

        const skeleton =
          widgetParams.container.parentNode.querySelector("#productSkeleton");
        if (skeleton) skeleton.remove();
      }

      widgetParams.container.innerHTML = "";

      if (hits.length === 0) {
        this.renderEmptyState(widgetParams.container, this._renderSeq);
      }

      hits.forEach((hit) => {
        const card = this.productCardTemplate.content.cloneNode(true);

        const handle = hit.handle || "";
        const imageUrl =
          hit.images?.[0]?.url ??
          hit.featured_image?.url ??
          this.placeholderImage;
        const title = hit.title || hit.product_title || "";
        const catalogId = this.catalogGids?.[0]?.split("/").pop();
        const priceDollars =
          hit[`price_catalog_${catalogId}`] ?? hit.min_price ?? hit.price;
        const price = priceDollars ? `$${priceDollars.toFixed(2)}` : "";
        const comparePrice = hit.compare_at_price
          ? `$${hit.compare_at_price.toFixed(2)}`
          : "";
        const productUrl = this.collectionHandle
          ? `/collections/${this.collectionHandle}/products/${handle}`
          : `/products/${handle}`;

        card
          .querySelectorAll("[data-product-url]")
          .forEach((el) => el.setAttribute("href", productUrl));

        const img = card.querySelector("[data-product-image]");
        if (img) {
          img.setAttribute("src", shopifyImageUrl(imageUrl, 400));
          img.setAttribute(
            "srcset",
            PRODUCT_IMAGE_WIDTHS.map(
              (w) => `${shopifyImageUrl(imageUrl, w)} ${w}w`,
            ).join(", "),
          );
          img.setAttribute(
            // Fixed px past 768px: the .container caps at --container-max (1232px), so a vw-based
            // value keeps growing past that width even though the column itself stops growing.
            "sizes",
            "(min-width: 768px) 320px, 50vw",
          );
          img.setAttribute("alt", title);
        }

        const titleLink = card.querySelector("[data-product-title-link]");
        if (titleLink) {
          titleLink.setAttribute("href", productUrl);
          titleLink.textContent = title;
        }

        const priceEl = card.querySelector("[data-product-price]");
        if (priceEl) priceEl.textContent = price;

        const comparePriceEl = card.querySelector(
          "[data-product-compare-price]",
        );
        if (comparePrice) {
          if (comparePriceEl) comparePriceEl.textContent = comparePrice;
        } else {
          comparePriceEl?.remove();
        }

        widgetParams.container.appendChild(card);
      });

      if (widgetParams.loadMoreContainer) {
        widgetParams.loadMoreContainer.style.display =
          isLastPage || hits.length === 0 ? "none" : "";
      }
    }

    // Zero-hits handler. Renders the truthful generic empty state immediately,
    // then — only when a vehicle fitment is active AND a verification query proves
    // there ARE matching products once the fitment clause is dropped — upgrades to
    // the distinct "no results for your vehicle" state.
    renderEmptyState(container, seq) {
      this.renderGenericEmpty(container);

      const vehicle = this.readActiveVehicle();
      if (!vehicle || !search.helper) return;

      const filters = search.helper.state.filters;
      const stripped = this.stripFitmentClause(filters);
      // No fitment clause present -> the empty result isn't attributable to the
      // vehicle filter, so there's nothing to distinguish. Leave the generic state.
      if (stripped === filters) return;

      // One verification query: the SAME search parameters (query, refinements,
      // collection scope) minus only the fitment facet clause. searchOnce reuses
      // the instant-search client and current state; it does NOT re-render the grid.
      search.helper
        .searchOnce({ filters: stripped, hitsPerPage: 0, page: 0 })
        .then((res) => {
          // Guard against a stale resolve overwriting a newer view. THREE checks:
          // 1. seq unchanged -> no render (empty or non-empty) happened since we
          //    launched; a later non-empty render (post clear / filter change)
          //    bumps _renderSeq and disqualifies this callback.
          // 2. container still in the DOM.
          // 3. the fitment context we queried for is STILL the active one — same
          //    active vehicle AND the same live filters string — so we never paint
          //    "No results for your {old vehicle}" after the fitment changed.
          if (seq !== this._renderSeq || !container.isConnected) return;
          const live = this.readActiveVehicle();
          if (!live || live.display !== vehicle.display) return;
          if (search.helper?.state?.filters !== filters) return;

          const nbHits = res?.content?.nbHits ?? 0;
          // unfiltered > 0 -> zero result is caused by the active vehicle filter.
          // unfiltered == 0 -> genuinely empty scope; keep the generic state.
          if (nbHits > 0) this.renderFitmentEmpty(container, vehicle);
        })
        .catch(() => {});
    }

    renderGenericEmpty(container) {
      const p = document.createElement("p");
      p.className = "collection__empty-state";
      p.textContent = this.noResultsText;
      container.appendChild(p);
    }

    renderFitmentEmpty(container, vehicle) {
      container.innerHTML = "";

      const wrap = document.createElement("div");
      wrap.className =
        "collection__empty-state collection__empty-state--fitment";

      const heading = document.createElement("p");
      heading.className = "collection__empty-heading";
      heading.textContent = `No results for your ${vehicle.display}`;
      wrap.appendChild(heading);

      const actions = document.createElement("div");
      actions.className = "collection__empty-actions";

      const clearBtn = document.createElement("button");
      clearBtn.type = "button";
      clearBtn.className = "btn btn--primary";
      clearBtn.innerHTML = '<span class="btn__label">Clear vehicle</span>';
      clearBtn.addEventListener("click", () => this.clearVehicle());

      const shopBtn = document.createElement("button");
      shopBtn.type = "button";
      shopBtn.className = "btn btn--dark";
      shopBtn.innerHTML = '<span class="btn__label">Shop your vehicle</span>';
      shopBtn.addEventListener("click", () =>
        this.shopVehicle(vehicle, shopBtn),
      );

      actions.appendChild(clearBtn);
      actions.appendChild(shopBtn);
      wrap.appendChild(actions);
      container.appendChild(wrap);
    }

    // The FULL raw record: FitmentCollections.forVehicle routes on `id`, so a
    // reader that strips it cannot drive "Shop your vehicle". FyreFitment.getVehicle
    // returns the record as saved (and self-heals a missing `display`, which a
    // vehicle stored before that field existed lacks — without it this recovery UI
    // silently never renders, leaving a dead-end empty state for exactly the stale
    // vehicles the fitment filter fails closed on).
    readActiveVehicle() {
      const v = window.FyreFitment.getVehicle();
      return v && v.display ? v : null;
    }

    // Drop the fitment facet clause (position-independent) from an Algolia
    // `filters` string, preserving every other AND-joined clause (status, scope).
    // WHY `includes` not `startsWith`: for a moto vehicle FyreFitment.vehicleFilter
    // emits a parenthesised OR group — `((metafield_fitment_vehicles:"X" OR
    // metafield_fitment_universal:true) OR product_type:"Fluid and Lubricant" OR
    // product_type:"Temperature Logging")` — which does not start with the bare
    // facet name. Match the group by substring, so both shapes strip.
    stripFitmentClause(filters) {
      if (!filters) return filters;
      return filters
        .split(" AND ")
        .filter((c) => !c.includes("metafield_fitment_vehicles:"))
        .join(" AND ");
    }

    // Clear the active fitment: mirror the vehicle selectors' remove path exactly
    // (drop storage + fire the cross-surface bus). The instant-search bridge
    // listens on `fitment:changed` and re-searches in place — no reload.
    clearVehicle() {
      // Invalidate any in-flight verification query BEFORE clearing, so a clear
      // can't be overtaken by its own stale verifier resolving after the re-search.
      this._renderSeq++;
      // Drops storage AND fires `fitment:changed` with a null detail — the one
      // cross-surface contract. The instant-search bridge re-searches in place.
      window.FyreFitment.clearVehicle();
    }

    // Route exactly like the header fitment form does today: forVehicle ->
    // VPP collection when confident, else /search (which reads the saved vehicle
    // from localStorage and applies the facet). Phase 1 will swap this for the
    // precomputed vehicle->VPP map; using forVehicle now keeps it identical.
    shopVehicle(vehicle, btn) {
      const creds = this.storefrontCreds();
      if (!window.FitmentCollections || !vehicle.id || !creds) {
        window.location.href = "/search";
        return;
      }
      if (btn) btn.disabled = true;
      window.FitmentCollections.forVehicle(vehicle.id, creds.url, creds.token)
        .then((coll) => {
          window.location.href = coll ? `/collections/${coll}` : "/search";
        })
        .catch(() => {
          window.location.href = "/search";
        });
    }

    // Reuse the Storefront credentials the vehicle-finder-header already ships on
    // the page (present in the header group on collection + search pages) so we
    // add no new hardcoded token copy.
    storefrontCreds() {
      const el = document.querySelector(
        "[data-storefront-url][data-storefront-token]",
      );
      if (!el) return null;
      const url = el.dataset.storefrontUrl;
      const token = el.dataset.storefrontToken;
      return url && token ? { url, token } : null;
    }
  }

  customElements.define("algolia-infinite-hits", AlgoliaInfiniteHits);
}
